/**
 * The Dutch a person reads, and the colours the two timelines wear.
 *
 * Enum values stay English snake_case (the app-wide invariant); every label here
 * is display only. The week's activity list marks each row with its person's
 * colour, so the parents' schedules read apart at a glance without splitting
 * the list into columns (docs/AGENDA.md §3); the change log (`ChangesScreen`,
 * `changesGrouping.ts`) reuses the same palette for its per-card avatar.
 */

/**
 * The palette parents are coloured from, by their order in the household. Two
 * parents get the blue/orange pair the design file draws (🔵 Jurren / 🟠 Olga);
 * more than two wrap through the rest.
 */
export const PARENT_PALETTE = ['#2563eb', '#ea7317', '#7a5cd0', '#2e9e8f'] as const;

/**
 * External caregivers without a colour of their own share one neutral tone —
 * and it has to be *neutral*, which the first choice (`#d98324`) was not.
 *
 * That amber sat on the warning axis: `--warn` is `#9a5b12` and `--warn-soft`
 * `#fbf0e0`, so a perfectly ordinary "bij Femke 19:00–22:00" window was tinted
 * in the one wash this design system reserves, in writing, for a problem —
 * "Don't render a successful outcome in amber". It was also a near-neighbour of
 * `olga-orange`, so an external sitter read as a parent at a glance. In dark
 * mode, at the stronger mix that file uses, it went further still and rendered
 * as a maroon panel that looked like an error.
 *
 * `--text-dim` is the app's existing neutral ink, so this introduces no new
 * colour: an external caregiver is quiet and grey, a parent caring for the child
 * keeps their own blue or orange, and "one of us" versus "someone else" becomes
 * a distinction you can see. The per-person `color` column still overrides it —
 * though nothing in the UI writes one today, which is why this fallback is what
 * every external caregiver actually wears.
 */
export const CAREGIVER_COLOR = '#6b665e';

/** A parent's colour from their position among the members. */
export function parentColor(userId: string, memberIds: string[]): string {
  const i = memberIds.indexOf(userId);
  return PARENT_PALETTE[(i >= 0 ? i : 0) % PARENT_PALETTE.length]!;
}

/**
 * The paint for an activity row's left spine — whose activity this is, readable
 * before a word of the row is.
 *
 * DESIGN.md gives the system a "3–4px coloured spine that encodes a verb or a
 * module", and `.agenda-item`'s own CSS comment has claimed for a while to be
 * "a row with a coloured spine that tells its nature" — but none was ever drawn,
 * so identity lived only in the dim meta line under the title, below the fold of
 * a glance.
 *
 * Several people share the spine by splitting it into hard-edged bands rather
 * than blending: a gradient with soft stops would invent colours belonging to
 * nobody. One person is a solid bar, two is half-and-half, and the order follows
 * the same household preference the name line uses, so the two agree.
 */
export function spinePaint(colors: string[]): string {
  if (colors.length === 0) return 'transparent';
  if (colors.length === 1) return colors[0]!;
  const step = 100 / colors.length;
  const stops = colors.map((c, i) => `${c} ${i * step}% ${(i + 1) * step}%`).join(', ');
  return `linear-gradient(to bottom, ${stops})`;
}

/**
 * The order the household reads two names in when both are on one activity:
 * Olga before Jurren, always, however they were tapped in (2026-08-30 request).
 * It is a display-only sort — the stored participant order is left untouched
 * (the server deliberately keeps it stable, see `db/agenda.ts`), so this also
 * tidies activities saved before the preference existed, on both surfaces that
 * show an activity's people together: the week's "wie" line and the change
 * log's detail line. Matching is case-insensitive; a name the list does not
 * know keeps its position (the sort is stable), so an unfamiliar household
 * degrades to insertion order rather than being reshuffled.
 */
export const PARTICIPANT_NAME_ORDER = ['Olga', 'Jurren'] as const;

function participantNameRank(name: string): number {
  const i = PARTICIPANT_NAME_ORDER.findIndex((n) => n.toLowerCase() === name.trim().toLowerCase());
  return i === -1 ? PARTICIPANT_NAME_ORDER.length : i;
}

/** Stable-sort items into the household's preferred participant order (above). */
export function orderByParticipantName<T>(items: readonly T[], nameOf: (item: T) => string): T[] {
  return [...items].sort((a, b) => participantNameRank(nameOf(a)) - participantNameRank(nameOf(b)));
}
